import { Info, CircleAlert, CircleX, CircleCheck, X } from 'lucide-react';
import { useEffect } from 'react';

const DEFAULT_DURATION = 5000;

const icons = {
  info: <Info className="size-6 text-blue-500" aria-hidden="true" />,
  success: <CircleCheck className="size-6 text-green-500" aria-hidden="true" />,
  warning: (
    <CircleAlert className="size-6 text-yellow-500" aria-hidden="true" />
  ),
  error: <CircleX className="size-6 text-red-500" aria-hidden="true" />,
};

export type NotificationProps = {
  notification: {
    id: string;
    type: keyof typeof icons;
    title: string;
    message?: string;
    duration?: number;
  };
  onDismiss: (id: string) => void;
};

export const Notification = ({
  notification: { id, type, title, message, duration },
  onDismiss,
}: NotificationProps) => {
  useEffect(() => {
    const timeout = setTimeout(() => {
      onDismiss(id);
    }, duration ?? DEFAULT_DURATION);

    return () => clearTimeout(timeout);
  }, [id, duration, onDismiss]);

  return (
    <div className="flex w-full flex-col items-center space-y-4 lg:items-end">
      <div className="pointer-events-auto w-full max-w-none sm:max-w-sm overflow-hidden rounded-lg rounded-t-none sm:rounded-t-lg lg:rounded-r-none bg-white shadow-lg ring-1 ring-black/5">
        <div className="p-4" role="alert" aria-label={title}>
          <div className="flex items-start">
            <div className="shrink-0">{icons[type]}</div>
            <div className="ml-3 w-0 flex-1 pt-0.5">
              <p className="text-sm font-medium text-gray-900 truncate">
                {title}
              </p>
              {message ? (
                <p className="mt-1 text-sm text-gray-500 line-clamp-5">
                  {message}
                </p>
              ) : null}
            </div>
            <div className="ml-4 flex shrink-0">
              <button
                className="inline-flex rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                onClick={() => onDismiss(id)}
                aria-label="Dismiss notification"
              >
                <span className="sr-only">Close</span>
                <X className="size-5" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
